import { useAppDispatch, useAppSelector } from '@store';
import {
  deleteFolderFile,
  duplicateFolderFile,
  sendFileEmail,
  updateFolderFile,
} from '@store/slices/foldersSlice';
import { notifications } from '@mantine/notifications';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router';
import { useUser } from '@/contexts/UserContext';
import { useLoading } from '@/contexts/LoadingContext';
import routes from '@/commons/routes';
import { myFetch } from '@/commons/fetch';
import { File as FileType } from '@/clients/pages/FolderPage/types';
import { handleOrganizePhotosInDigiStorage as organizePhotosInDigiStorage } from '@/commons/storageApi';
import { generateFilenameWithPrefixes } from '@/commons/generateFilenameWithPrefixes';

export const useFileActionsRedux = (folder: any, file: FileType) => {
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const navigate = useNavigate();
  const { user } = useUser();
  const { setIsLoading } = useLoading();
  const { settings } = useAppSelector((state) => state.settings);

  const handleOrganizePhotosInDigiStorage = async (
    photosList: string[] | Array<{ originalName: string; newName: string }>,
    folderPath: string,
    newFolderName: string
  ) => {
    try {
      setIsLoading(true);
      if (!settings?.digiStorageToken) {
        throw new Error(
          intl.formatMessage({ id: 'digiStorageTokenRequired' }) ||
            'DigiStorage token is required. Please generate a token in Settings.'
        );
      }
      await organizePhotosInDigiStorage(
        settings.digiStorageToken,
        folderPath,
        newFolderName,
        photosList
      );
    } catch (error: any) {
      notifications.show({
        color: 'red',
        title: intl.formatMessage({ id: 'error' }),
        message:
          error.message || intl.formatMessage({ id: 'errorOrganizingPhotos' }),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePublish = async () => {
    try {
      await dispatch(
        updateFolderFile({
          folderId: folder.id,
          fileId: file.id,
          updates: { isPublished: !file.isPublished },
        })
      ).unwrap();

      notifications.show({
        color: 'green',
        title: intl.formatMessage({ id: 'success' }),
        message: intl.formatMessage(
          { id: file.isPublished ? 'fileUnpublished' : 'filePublished' },
          { name: file.name }
        ),
      });
    } catch (error: any) {
      notifications.show({
        color: 'red',
        title: intl.formatMessage({ id: 'error' }),
        message: error.message,
      });
    }
  };

  const handleLock = async () => {
    try {
      await dispatch(
        updateFolderFile({
          folderId: folder.id,
          fileId: file.id,
          updates: { isLocked: !file.isLocked },
        })
      ).unwrap();

      notifications.show({
        color: 'green',
        title: intl.formatMessage({ id: 'success' }),
        message: intl.formatMessage(
          { id: file.isLocked ? 'fileUnlocked' : 'fileLocked' },
          { name: file.name }
        ),
      });
    } catch (error: any) {
      notifications.show({
        color: 'red',
        title: intl.formatMessage({ id: 'error' }),
        message: error.message,
      });
    }
  };

  const handleSign = async () => {
    try {
      await dispatch(
        updateFolderFile({
          folderId: folder.id,
          fileId: file.id,
          updates: { isSigned: true },
        })
      ).unwrap();

      notifications.show({
        color: 'green',
        title: intl.formatMessage({ id: 'success' }),
        message: intl.formatMessage({ id: 'fileSigned' }, { name: file.name }),
      });
    } catch (error: any) {
      notifications.show({
        color: 'red',
        title: intl.formatMessage({ id: 'error' }),
        message: error.message,
      });
    }
  };

  const handleVerify = async () => {
    try {
      await dispatch(
        updateFolderFile({
          folderId: folder.id,
          fileId: file.id,
          updates: { isVerified: true },
        })
      ).unwrap();

      notifications.show({
        color: 'green',
        title: intl.formatMessage({ id: 'success' }),
        message: intl.formatMessage(
          { id: 'fileVerified' },
          { name: file.name }
        ),
      });
    } catch (error: any) {
      notifications.show({
        color: 'red',
        title: intl.formatMessage({ id: 'error' }),
        message: error.message,
      });
    }
  };

  const handleDownload = async () => {
    setIsLoading(true);
    try {
      const base64PDF = await myFetch(
        `/folders/${folder.id}/files/${file.id}.pdf`
      );

      const pdfBytes = new Uint8Array(
        atob(base64PDF)
          .split('')
          .map((char) => char.charCodeAt(0))
      );
      const pdfBlob = new Blob([pdfBytes], { type: 'application/pdf' });
      const pdfURL = URL.createObjectURL(pdfBlob);

      const a = document.createElement('a');

      // Generate filename with prefixes from clasa and liceu fields
      const fileName = generateFilenameWithPrefixes(file);

      a.href = pdfURL;
      a.download = fileName;
      a.textContent = fileName;
      a.click();

      // Clean up the URL object
      URL.revokeObjectURL(pdfURL);

      notifications.show({
        color: 'green',
        title: intl.formatMessage({ id: 'success' }),
        message: intl.formatMessage(
          { id: 'downloadedAsPdf' },
          { name: file.name }
        ),
      });
    } catch (error: any) {
      notifications.show({
        color: 'red',
        title: intl.formatMessage({ id: 'error' }),
        message: intl.formatMessage(
          { id: 'errorDownloadingPdf' },
          { name: file.name }
        ),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendEmail = async (recipientEmail: string) => {
    try {
      setIsLoading(true);

      await dispatch(
        sendFileEmail({
          folderId: folder.id,
          fileId: file.id,
          to: recipientEmail,
        })
      ).unwrap();

      notifications.show({
        color: 'green',
        title: intl.formatMessage({ id: 'success' }),
        message: intl.formatMessage({ id: 'emailSent' }, { name: file.name }),
      });
    } catch (error: any) {
      notifications.show({
        color: 'red',
        title: intl.formatMessage({ id: 'error' }),
        message: error.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleImportFields = async (fieldsToImport: any[]) => {
    try {
      // Get current file fields
      const currentFields = (file as any).fields || [];

      // Merge with imported fields
      const mergedFields = [...currentFields, ...fieldsToImport];

      // Update file with merged fields
      await dispatch(
        updateFolderFile({
          folderId: folder.id,
          fileId: file.id,
          updates: { fields: mergedFields },
        })
      ).unwrap();

      notifications.show({
        color: 'green',
        title: intl.formatMessage({ id: 'success' }),
        message: intl.formatMessage(
          { id: 'importedFields' },
          { name: file.name }
        ),
      });
    } catch (error: any) {
      notifications.show({
        color: 'red',
        title: intl.formatMessage({ id: 'error' }),
        message: error.message,
      });
    }
  };

  const handleEditData = () => {
    if (!user?.username) return;
    navigate(
      routes.routes.editFileData(user.username, folder.folderName, file.id)
    );
  };

  const handleEditStructure = () => {
    if (!user?.username) return;
    navigate(
      routes.routes.editFileStructure(user.username, folder.id, file.id)
    );
  };

  const handleDuplicate = async () => {
    try {
      await dispatch(
        duplicateFolderFile({
          folderId: folder.id,
          fileId: file.id,
        })
      ).unwrap();

      notifications.show({
        color: 'green',
        title: intl.formatMessage({ id: 'success' }),
        message: intl.formatMessage(
          { id: 'fileDuplicated' },
          { name: file.name }
        ),
      });
    } catch (error: any) {
      notifications.show({
        color: 'red',
        title: intl.formatMessage({ id: 'error' }),
        message: error.message,
      });
    }
  };

  const handleRename = async (newName: string) => {
    try {
      await dispatch(
        updateFolderFile({
          folderId: folder.id,
          fileId: file.id,
          updates: { name: newName },
        })
      ).unwrap();

      notifications.show({
        color: 'green',
        title: intl.formatMessage({ id: 'success' }),
        message: intl.formatMessage({ id: 'fileRenamed' }, { name: file.name }),
      });
    } catch (error: any) {
      notifications.show({
        color: 'red',
        title: intl.formatMessage({ id: 'error' }),
        message: error.message,
      });
    }
  };

  const handleDelete = async () => {
    try {
      await dispatch(
        deleteFolderFile({
          folderId: folder.id,
          fileId: file.id,
        })
      ).unwrap();

      notifications.show({
        color: 'green',
        title: intl.formatMessage({ id: 'success' }),
        message: intl.formatMessage({ id: 'fileDeleted' }, { name: file.name }),
      });
    } catch (error: any) {
      notifications.show({
        color: 'red',
        title: intl.formatMessage({ id: 'error' }),
        message: error.message,
      });
    }
  };

  return {
    handleOrganizePhotosInDigiStorage,
    handlePublish,
    handleLock,
    handleSign,
    handleVerify,
    handleDownload,
    handleSendEmail,
    handleImportFields,
    handleEditData,
    handleEditStructure,
    handleDuplicate,
    handleRename,
    handleDelete,
  };
};
